import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { CSSTransition, SwitchTransition } from "react-transition-group";

import { noop } from "@/utils";

import styles from "./Wizard.module.css";

const WizardContext = createContext<{
  previous: () => void;
  next: () => void;
  to: (n: number) => void;
  step: number;
}>({
  previous: noop,
  next: noop,
  to: noop,
  step: 0,
});
export const useWizard = () => useContext(WizardContext);

export type WizardProps = {
  steps: React.FunctionComponent[];
  className?: string;
  onStepChange?: (step: number) => void;
  render?: (r: React.ReactNode) => React.ReactNode;
};

const Inner = ({ children, ...props }: React.HTMLProps<HTMLDivElement>) => {
  const { step } = useWizard();
  const nodeRef = useRef<HTMLDivElement>(null);

  return (
    <SwitchTransition>
      <CSSTransition
        timeout={500}
        key={step}
        nodeRef={nodeRef}
        addEndListener={(done) => {
          nodeRef.current?.addEventListener("transitionend", done, false);
        }}
        classNames={{
          enter: styles.enter,
          enterActive: styles.enterActive,
          exit: styles.exit,
          exitActive: styles.exitActive,
        }}
      >
        <div ref={nodeRef} {...props}>
          {children}
        </div>
      </CSSTransition>
    </SwitchTransition>
  );
};

const Wizard: React.FunctionComponent<WizardProps> = ({
  steps,
  onStepChange,
  render,
  ...props
}) => {
  const [step, setStep] = useState(0);
  const Step = useMemo(() => steps[step], [step, steps]);

  useEffect(() => {
    if (onStepChange) onStepChange(step);
  }, [step, onStepChange]);

  return (
    <WizardContext.Provider
      value={{
        step,
        previous: () => setStep((v) => Math.max(0, v - 1)),
        next: () => setStep((v) => Math.min(steps.length - 1, v + 1)),
        to: (v) => setStep(Math.max(0, Math.min(steps.length - 1, v))),
      }}
    >
      {render ? (
        render(
          <Inner {...props}>
            <Step />
          </Inner>
        )
      ) : (
        <Inner {...props}>
          <Step />
        </Inner>
      )}
    </WizardContext.Provider>
  );
};

export default Wizard;
